import { getCurrencySymbol, AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
  input,
  output,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { DrawerModule } from 'primeng/drawer';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  interval,
  map,
  of,
  Subscription,
  take,
  takeUntil,
  Subject,
} from 'rxjs';

import type {
  Client,
  ClientSubscriptionFreezeResponse,
  GetClientSubscriptionResponse,
  InvoiceOccurance,
  PlanBreakUpCalculationResponse,
  StopClientSubscriptionFreezeRequest,
  CancelClientSubscriptionFreezeRequest,
  PosDeviceSale,
  PaymentGatewatMaster,
  PosTerminal,
  GetOrderStatusOfQuotation,
} from '@fitness-force/models';
import {
  AuthSevices,
  ClientService,
  TimeconversionService,
  ToastMessageService,
  CommonService,
  PlansubscriptionService,
  FreezeService,
  PaymentGatewayMasterService,
  extractApiErrorMessages,
} from '@fitness-force/api';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import {
  ClientSubscriptionFreezeStatus,
  ClientSubscriptionFreezeType,
  RecurringOccuranceType
} from '@fitness-force/enum';

import { ErrorsComponent } from '@fitness-force/errors';
import { AppErrors } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { PaymentSignUp } from '@fitness-force/models';
import {
  OtherPaymentComponent,
  SavedPaymentMethodsPopupComponent,
} from '@fitness-force/common/payment';
import { FreezeReasonService } from '../../services/freeze-reason.service';
import type {
  FreezeReason,
  CreateFreezeRequest,
  ApproveFreezeRequest,
  FreezeRequestDetails,
  FreezeRequestItem,
  FreezeChargeBreakupRequest,
  FreezeCycleAdjustmentResponse,
  CalculateFreezeAdjustmentRequest,
  RescheduleFreezeRequest,
} from '../../models/freeze-reason.model';
import {
  CancelAppointmentClassBooking,
  FreezeMode,
  FreezeRequestStatus,
  RecurringTemplateAdjustmentType,
} from '../../enums/freeze.enum';
import { InputNumberModule } from 'primeng/inputnumber';
import { Router } from '@angular/router';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { FeatureFlagService, RouteStateService } from '@fitness-force/shared-utils';
import { FreezeClientInfoComponent } from '../freeze-client-info/freeze-client-info.component';
import { FreezeCycleAdjustmentDetailsComponent } from '../freeze-cycle-adjustment-details/freeze-cycle-adjustment-details.component';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { environment } from '@fitness-force/environments';
import {
  PosDeviceListComponent,
  PosDeviceListCloseEvent,
  OrderSubscriptionStatusPollingService,
  getOnlinePaymentFailureReason,
  type SignupPaymentPollTerminalOutcome,
} from '@fitness-force/billing';

// Extracted services
import { FreezeFormService } from '../../services/freeze-form.service';
import { FreezeChargeService, ProcessedCharge } from '../../services/freeze-charge.service';
import { FreezeDateService } from '../../services/freeze-date.service';
import { FreezeFileService, UploadedFreezeFile } from '../../services/freeze-file.service';
import { FreezePermissionService } from '../../services/freeze-permission.service';

// Extracted child components
import { FreezeChargeBreakdownComponent } from '../freeze-charge-breakdown/freeze-charge-breakdown.component';
import { FreezeFileUploadComponent } from '../freeze-file-upload/freeze-file-upload.component';
import { FreezeDateSelectionComponent } from '../freeze-date-selection/freeze-date-selection.component';
import { FreezeCommentsComponent } from '../freeze-comments/freeze-comments.component';
import {
  getFreezeTargetYears,
  getYearWiseFreezeRemaining,
  validateFreezeYearsHaveRemainingAttempts,
} from '../../utils/freeze-attempt.util';

@Component({
  selector: 'enhanced-freeze-sidebar',
  templateUrl: './enhanced-freeze-sidebar.component.html',
  styleUrls: ['./enhanced-freeze-sidebar.component.scss'],
  providers: [ToastMessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    StyleClassModule,
    DrawerModule,
    AvatarModule,
    SelectModule,
    ButtonModule,
    DatePickerModule,
    TableModule,
    MultiSelectModule,
    MessageModule,
    InputTextModule,
    ProgressSpinnerModule,
    DialogModule,
    ErrorsComponent,
    ErrorMsgComponent,
    OtherPaymentComponent,
    TranslateModule,
    InputNumberModule,
    SkeletonModule,
    TagModule,
    TooltipModule,
    AsyncPipe,
    FreezeClientInfoComponent,
    FreezeCycleAdjustmentDetailsComponent,
    FreezeChargeBreakdownComponent,
    FreezeFileUploadComponent,
    FreezeDateSelectionComponent,
    FreezeCommentsComponent,
    PosDeviceListComponent,
    SavedPaymentMethodsPopupComponent,
    DateTimeFormatPipe,
],
})
export class EnhancedFreezeSidebarComponent implements OnInit, OnDestroy {
  // ─── Injected Services ───────────────────────────────────────────────────────
  private freezeReasonService = inject(FreezeReasonService);
  private freezeService = inject(FreezeService);
  private clientService = inject(ClientService);
  private translate = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);
  private store = inject(Store);
  private toastMessageService = inject(ToastMessageService);
  private timeService = inject(TimeconversionService);
  private commonService = inject(CommonService);
  private router = inject(Router);
  private plansubscriptionService = inject(PlansubscriptionService);
  private featureFlagService = inject(FeatureFlagService);
  private paymentGatewayMasterService = inject(PaymentGatewayMasterService);
  private stateService = inject(RouteStateService);

  // Extracted services
  private freezeFormService = inject(FreezeFormService);
  private freezeChargeService = inject(FreezeChargeService);
  private freezeDateService = inject(FreezeDateService);
  private freezeFileService = inject(FreezeFileService);
  private freezePermissionService = inject(FreezePermissionService);
  private orderSubscriptionStatusPollingService = inject(
    OrderSubscriptionStatusPollingService
  );

  authService = inject(AuthSevices);

  // ─── Inputs & Outputs ────────────────────────────────────────────────────────
  readonly visible = input<boolean>(false);
  readonly client = input<Client>({} as Client);
  readonly cancelBookingType = input<string>('');
  readonly subscription = input<GetClientSubscriptionResponse>(
    {} as GetClientSubscriptionResponse
  );
  readonly tenantId = input<number>(0);
  readonly mode = input<
    'request' | 'freeze' | 'approve' | 'reschedule' | 'stop' | 'cancel'
  >('freeze');
  readonly freezeRequestId = input<number>();
  readonly freezeRequestItem = input<FreezeRequestItem>();
  readonly freezeToReschedule = input<ClientSubscriptionFreezeResponse>();
  readonly approveComments = input<{ clientVisible: string; internal: string }>();

  readonly hideSideBar = output<boolean>({ alias: 'onCloseSideBar' });
  readonly openPaymentTimeline = output<{
    uuid: string;
    skipPaymentPolling?: boolean;
  }>({ alias: 'onOpenPaymentTimeline' });

  // ─── Form ────────────────────────────────────────────────────────────────────
  fg: UntypedFormGroup = new UntypedFormGroup({});

  // ─── State ───────────────────────────────────────────────────────────────────
  showSidebar1 = true;
  freezeReasons$ = new BehaviorSubject<FreezeReason[]>([]);
  freezeReasonsLoading$ = new BehaviorSubject<boolean>(false);
  freezeTimeline$: BehaviorSubject<InvoiceOccurance[]> = new BehaviorSubject<InvoiceOccurance[]>([]);
  filteredFreezeTimeline$: BehaviorSubject<InvoiceOccurance[]> = new BehaviorSubject<InvoiceOccurance[]>([]);
  freeze$: BehaviorSubject<ClientSubscriptionFreezeResponse[]> = new BehaviorSubject<ClientSubscriptionFreezeResponse[]>([]);
  clientId$: BehaviorSubject<number> = new BehaviorSubject(0);
  isUpdated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  currentUserDetails: any;

  error = new BehaviorSubject(null as AppErrors | null);
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;

  minDate: Date = new Date();
  maxDate: Date = new Date();

  btnLoading = new BehaviorSubject(false);
  freezeDays = 0;
  selectionLimit = 10;
  format = 'yyyy-MM-ddTHH:mm:ss';

  // Payment
  showPaymentOptions = false;
  selectedPaymentMode: 'online' | 'offline' | null = null;
  showOtherPaymentsModel$ = new BehaviorSubject<boolean>(false);
  paymentAmount = 0;
  creditBalance = 0;

  showPayViaSavedPaymentMethods = false;
  showSavedPaymentMethodsPopup = new BehaviorSubject<boolean>(false);
  selectedCardOnFileTokenId: number | null = null;
  showChargeOnPosTerminal = false;
  showDeviceList = false;
  posGatewaysLoaded = false;
  posDeviceSale: PosDeviceSale | undefined;
  paymentGatewayList = new BehaviorSubject<PaymentGatewatMaster[]>([]);
  paymentGateway: PaymentGatewatMaster = {} as PaymentGatewatMaster;
  paymentFailed = false;
  paymentFailedMessages: string[] = [];
  transactionInProcess = false;
  paymentConfirmationPending = false;
  paymentConfirmationTimedOut = false;
  paymentConfirmationShowTimeLimit = false;
  paymentConfirmationSecondsRemaining = 0;
  private paymentConfirmationOrderUuid = '';
  private paymentConfirmationPosFlow = false;
  private pendingPaymentConfirmation = false;
  private paymentConfirmationCountdownSub?: Subscription;
  private readonly POS_PAYMENT_TIMEOUT_MS = 2 * 60 * 1000;
  private pendingPosPaymentMode: 'freeze' | 'approve' | null = null;
  isSubmitting$ = new BehaviorSubject<boolean>(false);
  tenantDetails: any = {};

  // Currency
  currencySymbol = '$';
  currencyCode = 'USD';
  locale = 'en-US';

  // Approve mode
  freezeRequestDetails$ = new BehaviorSubject<FreezeRequestDetails | null>(null);
  freezeRequestLoading$ = new BehaviorSubject<boolean>(false);

  // Charges
  freezeChargeBreakup$ = new BehaviorSubject<PlanBreakUpCalculationResponse | null>(null);
  freezeChargeLoading$ = new BehaviorSubject<boolean>(false);
  signupCharges: ProcessedCharge[] = [];
  totalChargeAmount = 0;

  // Cycle adjustment
  freezeCycleAdjustment$ = new BehaviorSubject<FreezeCycleAdjustmentResponse | null>(null);
  freezeCycleAdjustmentLoading$ = new BehaviorSubject<boolean>(false);
  private lastValidCycleSelection: InvoiceOccurance[] = [];

  // Loading
  subscriptionLoading$ = new BehaviorSubject<boolean>(false);
  isInitialLoading$ = new BehaviorSubject<boolean>(true);

  // Discount UI state
  applyDiscount = false;
  discountApplied = false;
  inValidDiscount = false;
  isInclusive = false;
  single_staff_discount = 0;

  // File upload
  uploadedFiles: UploadedFreezeFile[] = [];
  isUploadingFile$ = new BehaviorSubject<boolean>(false);

  // Enum references for template
  FreezeMode = FreezeMode;
  FreezeRequestStatus = FreezeRequestStatus;

  private destroy$ = new Subject<void>();
  freezeV2Flow = false;



  // ─── Mode Helpers ────────────────────────────────────────────────────────────
  get isRescheduleMode(): boolean {
    return this.mode() === 'reschedule';
  }

  get isStopMode(): boolean {
    return this.mode() === 'stop';
  }

  get isCancelMode(): boolean {
    return this.mode() === 'cancel';
  }

  get isApproveMode(): boolean {
    return this.mode() === 'approve';
  }

  get approveRequestedStartDateRaw(): string | null {
    return this.freezeRequestDetails$.value?.requested_start_date
      ?? this.freezeRequestItem()?.requested_start_date
      ?? null;
  }

  get approveRequestedEndDateRaw(): string | null {
    const rawEndDate = this.approveRequestedEndDateRawValue;

    if (!rawEndDate) {
      return null;
    }

    return this.freezeDateService.resolveRequestedFreezeEndDate(
      this.approveRequestedStartDateRaw,
      rawEndDate,
      this.filteredFreezeTimeline$.value
    );
  }

  private get approveRequestedEndDateRawValue(): string | null {
    return this.freezeRequestDetails$.value?.requested_end_date
      ?? this.freezeRequestItem()?.requested_end_date
      ?? null;
  }

  get approveRequestedStartDate(): Date | null {
    const formDate = this.StartDateControl?.value;
    if (formDate) {
      return formDate instanceof Date ? formDate : new Date(formDate);
    }

    const rawDate = this.approveRequestedStartDateRaw;
    if (!rawDate) {
      return null;
    }

    return this.timeService.ConvertUTCToLocalDateTime(rawDate);
  }

  get approveRequestedEndDate(): Date | null {
    const formDate = this.EndDateControl?.value;
    if (formDate) {
      return formDate instanceof Date ? formDate : new Date(formDate);
    }

    const rawDate = this.approveRequestedEndDateRaw;
    if (!rawDate) {
      return null;
    }

    return this.timeService.ConvertUTCToLocalDateTime(rawDate);
  }

  hasApproveRequestedDates(): boolean {
    return !!(
      this.approveRequestedStartDateRaw
      || this.approveRequestedEndDateRaw
      || (this.StartDateControl?.value && this.EndDateControl?.value)
    );
  }

  get approveFreezeDays(): number {
    return this.freezeDateService.calculateFreezeDays(
      this.approveRequestedStartDate,
      this.approveRequestedEndDate
    );
  }

  get IsRecurringSubscription(): boolean {
    return this.freezeFormService.isRecurringSubscription(this.subscription());
  }

  get freezeStartDate(): Date | string | null {
    return this.freezeToReschedule()?.start_date ?? null;
  }

  get freezeEndDate(): Date | string | null {
    return this.freezeToReschedule()?.end_date ?? null;
  }

  // ─── Form Control Accessors ──────────────────────────────────────────────────
  get StartDateControl() {
    return this.freezeFormService.getFormControl(this.fg, 'startdate');
  }

  get EndDateControl() {
    return this.freezeFormService.getFormControl(this.fg, 'enddate');
  }

  get SelectedFreezeAttempt() {
    return this.freezeFormService.getFormControl(this.fg, 'selectedfreezeattempt');
  }

  get FreezeReasonControl() {
    return this.freezeFormService.getFormControl(this.fg, 'freezeReason');
  }

  get ClientVisibleCommentsControl() {
    return this.freezeFormService.getFormControl(this.fg, 'clientVisibleComments');
  }

  get InternalCommentsControl() {
    return this.freezeFormService.getFormControl(this.fg, 'internalComments');
  }

  get CommentControl() {
    return this.freezeFormService.getFormControl(this.fg, 'comment');
  }

  get StopDateControl() {
    return this.freezeFormService.getFormControl(this.fg, 'stopdate');
  }

  get ChargeAmountsFormArray() {
    return this.freezeFormService.getFormArray(this.fg, 'chargeAmounts');
  }

  get ChargeDiscountsFormArray() {
    return this.freezeFormService.getFormArray(this.fg, 'chargeDiscounts');
  }

  get stopDateMin(): Date {
    const freeze = this.freezeToReschedule();
    if (freeze?.start_date) {
      return this.timeService.ConvertUTCToLocalDateTime(freeze.start_date);
    }
    return new Date();
  }

  get stopDateMax(): Date {
    const freeze = this.freezeToReschedule();
    if (freeze?.end_date) {
      return this.timeService.ConvertUTCToLocalDateTime(freeze.end_date);
    }
    return new Date();
  }

  // ─── Derived State for Template ─────────────────────────────────────────────
  get CycleRemainingInYear(): Array<{ year: number; count: number }> {
    return getYearWiseFreezeRemaining({
      subscription: this.subscription(),
      billingOccurrences: this.freezeTimeline$.value,
      freezes: this.freeze$.value,
    }).map((entry) => ({ year: entry.year, count: entry.used }));
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.minDate = new Date();
    this.maxDate = new Date();
    this.currentUserDetails = this.authService.decodedToken;
    this.single_staff_discount = this.currentUserDetails.single_staff_discount || 0;
    // Resolve flag reactively so the correct value is always used, even if
    // Statsig finishes initialising after this component is created.
    this.featureFlagService.getFlag$('show_freeze_v2')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(isEnabled => {
        this.freezeV2Flow = isEnabled;
        this.componentInit();
      });

    this.featureFlagService.getFlag$('show_charge_on_terminal_or_pos')
      .pipe(takeUntil(this.destroy$))
      .subscribe((showChargeOnPosTerminal) => {
        this.showChargeOnPosTerminal = showChargeOnPosTerminal;
        this.cd.markForCheck();
      });

    this.featureFlagService.getFlag$('show_card_on_file')
      .pipe(takeUntil(this.destroy$))
      .subscribe((showCardOnFile) => {
        this.showPayViaSavedPaymentMethods = showCardOnFile;
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.clearPaymentConfirmationCountdown();
    this.orderSubscriptionStatusPollingService.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Initialization ──────────────────────────────────────────────────────────
  componentInit(): void {
    this.fg = this.freezeFormService.buildFreezeForm({
      isRecurringSubscription: this.IsRecurringSubscription,
      freezeCanOnlyBillingCycle:
        this.subscription()?.freeze_can_only_billing_cycle ?? false,
      freezeV2Flow: this.freezeV2Flow,
      isRescheduleMode: this.isRescheduleMode,
      isStopMode: this.isStopMode,
      isCancelMode: this.isCancelMode,
      isApproveMode: this.isApproveMode,
    });

    // Pre-fill dates for reschedule, stop, and cancel modes
    if ((this.isRescheduleMode || this.isStopMode || this.isCancelMode) && this.freezeToReschedule()) {
      const freeze = this.freezeToReschedule()!;
      this.fg.patchValue({
        startdate: this.timeService.ConvertUTCToLocalDateTime(freeze.start_date),
        enddate: this.timeService.ConvertUTCToLocalDateTime(freeze.end_date),
      });

      if (this.isStopMode) {
        const freezeStart = this.timeService.ConvertUTCToLocalDateTime(freeze.start_date);
        const freezeEnd = this.timeService.ConvertUTCToLocalDateTime(freeze.end_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let defaultStopDate: Date;
        if (today >= freezeStart && today <= freezeEnd) {
          defaultStopDate = today;
        } else if (today < freezeStart) {
          defaultStopDate = freezeStart;
        } else {
          defaultStopDate = freezeEnd;
        }
        this.fg.patchValue({ stopdate: defaultStopDate });
      }

      setTimeout(() => {
        this.calculateFreezeCycleAdjustment();
      }, 500);
    }

    this.store.select(COMPANY_INFO).subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);

      const currencyInfo = this.commonService.getCurrencyCodeAndLocale();
      this.currencyCode = currencyInfo.currencyCode;
      this.locale = currencyInfo.locale;
      this.currencySymbol = getCurrencySymbol(
        this.currencyCode,
        'narrow',
        this.locale
      );

      if (
        this.freezeV2Flow &&
        !this.isRescheduleMode &&
        !this.isStopMode &&
        !this.isCancelMode
      ) {
        this.loadFreezeReasons();
      } else {
        this.fg.patchValue({ freezeReason: 'Other' });
        this.checkInitialLoadingComplete();
      }
    });

    if (this.mode() === 'approve' && this.freezeRequestId()) {
      this.loadFreezeRequestDetails();
    }

    const clientId = this.getClientId();
    this.clientId$.next(clientId);

    const subscriptionId = this.subscription()?.id;

    if (subscriptionId) {
      this.loadFreezeHistoryForSubscription(clientId, subscriptionId);
    }

    if (
      this.IsRecurringSubscription &&
      this.subscription()?.freeze_can_only_billing_cycle
    ) {
      if (subscriptionId) {
        this.GetFreezeTimeline(clientId, subscriptionId);
      }
    }

    this.setupFormWatchers();
  }

  private setupFormWatchers(): void {
    // Update maxDate from start date
    this.StartDateControl?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value: any) => {
        this.maxDate = value;
      });

    // Watch billing cycle multiselect
    this.SelectedFreezeAttempt?.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedOptions) => {
        if (selectedOptions && selectedOptions.length > 0) {
          const selectableCycles: InvoiceOccurance[] =
            this.filteredFreezeTimeline$.value || [];
          if (
            !this.freezeDateService.areSelectedCyclesSequential(
              selectableCycles,
              selectedOptions
            )
          ) {
            this.toastMessageService.showErrorMessage(
              this.translate.instant('FREEZE.FREEZE_SIDEBAR.CYCLES_NOT_SEQUENTIAL')
            );
            this.SelectedFreezeAttempt?.setValue(
              this.lastValidCycleSelection,
              { emitEvent: false }
            );
            this.cd.detectChanges();
            return;
          }

          this.lastValidCycleSelection = selectedOptions;
          this.updateSelectionLimit(selectedOptions);
          if (this.isPaidFreeze()) {
            this.calculateFreezeCharges();
          }
          this.calculateFreezeCycleAdjustment(selectedOptions);
        } else {
          this.lastValidCycleSelection = [];
          this.freezeCycleAdjustment$.next(null);
          this.cd.detectChanges();
        }
      });

    // Watch freeze reason changes
    this.FreezeReasonControl?.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, curr) => prev?.reason_id === curr?.reason_id
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((reason) => {
        if (!this.isRescheduleMode) {
          if (
            this.isPaidFreeze()
          ) {
            this.calculateFreezeCharges();
          } else {
            this.clearFreezeCharges();
          }
        }
      });

    // Watch start date changes
    this.StartDateControl?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          !this.isRescheduleMode &&
          this.isPaidFreeze() &&
          this.EndDateControl?.value
        ) {
          this.calculateFreezeCharges();
        }
        if (this.EndDateControl?.value) {
          this.calculateFreezeCycleAdjustment();
        }
      });

    // Watch end date changes
    this.EndDateControl?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          !this.isRescheduleMode &&
          this.isPaidFreeze() &&
          this.StartDateControl?.value
        ) {
          this.calculateFreezeCharges();
        }
        if (this.StartDateControl?.value) {
          this.calculateFreezeCycleAdjustment();
        }
      });

    // Watch stop date changes (stop mode only)
    if (this.isStopMode) {
      this.StopDateControl?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.StartDateControl?.value) {
            this.calculateFreezeCycleAdjustment();
          }
        });
    }
  }

  private updateSelectionLimit(selectedOptions: InvoiceOccurance[]): void {
    const yearCounts: Record<number, number> = {};
    selectedOptions.forEach((option) => {
      const year = new Date(option.occurance_start_date).getFullYear();
      yearCounts[year] = (yearCounts[year] || 0) + 1;
    });

    for (const year in yearCounts) {
      const freezeYears = this.CycleRemainingInYear.find(
        (o: any) => o.year === Number(year)
      );
      if (freezeYears) {
        this.selectionLimit = this.freezePermissionService.isAdmin(
          this.currentUserDetails
        )
          ? 9999
          : this.subscription().freeze_allowed_number_of_attempts_in_year -
            freezeYears.count;
      }
    }
  }

  // ─── Data Loading ────────────────────────────────────────────────────────────
  loadFreezeHistoryForSubscription(clientId: number, subscriptionId: number): void {
    const tenantId = this.getTenantId();
    if (!tenantId || !clientId || !subscriptionId) return;

    this.freezeService.GetFreezeBySubscriptionId(tenantId, clientId, subscriptionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (freezes: ClientSubscriptionFreezeResponse[]) => {
          this.freeze$.next(freezes ?? []);
          this.cd.detectChanges();
        },
        error: () => {
          this.freeze$.next([]);
        }
      });
  }

  loadFreezeReasons(): void {
    const tenantId = this.getTenantId();
    this.freezeReasonsLoading$.next(true);

    this.freezeReasonService.getFreezeReasons(tenantId).subscribe({
      next: (response) => {
        const activeReasons = response ? response.filter((reason) => reason.is_active) : [];
        this.freezeReasons$.next(activeReasons);
        this.freezeReasonsLoading$.next(false);
        this.fg.patchValue({ freezeReason: activeReasons[0] || 'Other' });
        if (this.isApproveMode && this.freezeRequestDetails$.value) {
          this.populateFormFromFreezeRequest(this.freezeRequestDetails$.value);
        }
        this.checkInitialLoadingComplete();
        this.cd.markForCheck();
      },
      error: (error) => {
        this.fg.patchValue({ freezeReason: 'Other' });
        this.toastMessageService.showError(error);
        this.freezeReasonsLoading$.next(false);
        this.checkInitialLoadingComplete();
        this.cd.detectChanges();
      },
    });
  }

  loadFreezeRequestDetails(): void {
    const freezeRequestId = this.freezeRequestId();
    if (!freezeRequestId) return;

    this.freezeRequestLoading$.next(true);

    this.freezeReasonService
      .getFreezeRequestById(this.tenantId(), freezeRequestId)
      .subscribe({
        next: (response) => {
          this.freezeRequestDetails$.next(response);
          this.populateFormFromFreezeRequest(response);

          this.freezeRequestLoading$.next(false);
          this.checkInitialLoadingComplete();
          this.cd.markForCheck();
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.freezeRequestLoading$.next(false);
          this.checkInitialLoadingComplete();
          this.cd.detectChanges();
        },
      });
  }

  GetFreezeTimeline(clientId: number, subscriptionId: number): void {
    this.clientService
      .getPaymentTimeline(clientId, subscriptionId)
      .subscribe({
        next: (freezeTimeline: any) => {
          if (freezeTimeline?.invoice_occurance) {
            let filteredTimeline = freezeTimeline.invoice_occurance as InvoiceOccurance[];
            filteredTimeline = filteredTimeline.filter(
              (o) =>
                (o.occurance_type !== RecurringOccuranceType.Enhancement &&
                  o.occurance_type.toString() !== RecurringOccuranceType[RecurringOccuranceType.Enhancement])
            );
            const sortedOccurrences = this.freezeDateService.sortBillingCycles(filteredTimeline);
            const filteredData = sortedOccurrences.filter(
              (o) =>
                (o.occurance_invoice_id === 0 &&
                  o.occurance_type !== RecurringOccuranceType.Deposit &&
                  o.occurance_type.toString() !== RecurringOccuranceType[RecurringOccuranceType.Deposit]) ||
                ((o.occurance_type === RecurringOccuranceType.Deposit ||
                  o.occurance_type.toString() === RecurringOccuranceType[RecurringOccuranceType.Deposit]) &&
                  o.deposit_applied === false)
            );

            const timeline: InvoiceOccurance[] = filteredData.map(
              (obj: InvoiceOccurance) => {
                const newObj = { ...obj };
                newObj.is_frozen = obj.occurance_invoice_id === 0 && (obj.occurance_type === RecurringOccuranceType.Freeze || obj.occurance_type.toString() === RecurringOccuranceType[RecurringOccuranceType.Freeze]);
                const datetoutc = this.timeService.ConvertUTCToLocalDateTime(
                  obj.occurance_start_date
                );
                newObj.formated_start_date = this.toDate(datetoutc);
                const dateStr = new Date(datetoutc).toDateString();
                const dateStrArr = dateStr.split(' ');
                const s = ['th', 'st', 'nd', 'rd'];
                const v = parseInt(dateStrArr[2]) % 100;
                newObj.formated_date = `${dateStrArr[2] + (s[(v - 20) % 10] || s[v] || s[0])} of ${dateStrArr[1]} ${dateStrArr[3]}`;
                return newObj;
              }
            );

            this.freezeTimeline$.next(sortedOccurrences);
            this.filteredFreezeTimeline$.next(timeline);
            this.cd.detectChanges();
          } else {
            this.freezeTimeline$.next([]);
            this.filteredFreezeTimeline$.next([]);
            this.cd.detectChanges();
          }
        },
        error: () => {
          this.freezeTimeline$.next([]);
          this.filteredFreezeTimeline$.next([]);
          this.cd.detectChanges();
        },
      });
  }

  // ─── Form Population ─────────────────────────────────────────────────────────
  populateFormFromFreezeRequest(freezeRequest: FreezeRequestDetails): void {
    if (!freezeRequest) return;

    const matchingReason = this.freezeReasons$.value.find(
      (r) => r.reason_id === freezeRequest.frz_req_reason_id
    );

    // Use emitEvent: false to suppress reactive watcher triggers during bulk population.
    // All watchers (FreezeReasonControl, StartDateControl, EndDateControl) would each
    // independently trigger calculateFreezeCharges / calculateFreezeCycleAdjustment,
    // causing 3+ duplicate API calls on open. We call them once explicitly at the end.
    this.fg.patchValue({
      freezeReason: matchingReason,
      clientVisibleComments: this.approveComments()?.clientVisible || '',
      internalComments: this.approveComments()?.internal || '',
      startdate: this.timeService.ConvertUTCToLocalDateTime(
        freezeRequest.requested_start_date
      ),
      enddate: this.timeService.ConvertUTCToLocalDateTime(
        freezeRequest.requested_end_date
      ),
    }, { emitEvent: false });

    if (
      freezeRequest.approved_start_date &&
      freezeRequest.approved_end_date
    ) {
      this.fg.patchValue({
        startdate: this.timeService.ConvertUTCToLocalDateTime(
          freezeRequest.approved_start_date
        ),
        enddate: this.timeService.ConvertUTCToLocalDateTime(
          freezeRequest.approved_end_date
        ),
      }, { emitEvent: false });
    }

    // In approve mode: trigger charge breakup and cycle adjustment exactly ONCE
    // after all form values have been patched silently above.
    if (this.isApproveMode) {
      setTimeout(() => {
        if (this.isPaidFreeze()) {
          this.calculateFreezeCharges();
        }
        this.calculateFreezeCycleAdjustment();
      }, 300);
    }

    this.cd.markForCheck();
  }

  // ─── Charge Calculation ──────────────────────────────────────────────────────
  isPaidFreeze(): boolean {
    const subscription = this.subscription();
    return (
      subscription.freeze_type === ClientSubscriptionFreezeType.PaidFreeze ||
      subscription.freeze_type.toString() ===
        ClientSubscriptionFreezeType[ClientSubscriptionFreezeType.PaidFreeze]
    );
  }

  calculateFreezeCharges(): void {
    if (!this.isPaidFreeze()) return;

    let chargeMasterId: number;
    let reasonId: number;

    if (this.mode() === 'approve') {
      // In approve mode: use frz_req_charge_master_id from the freeze request details
      const freezeRequestDetails = this.freezeRequestDetails$.value;
      if (!freezeRequestDetails?.frz_req_charge_master_id || freezeRequestDetails.frz_req_charge_master_id <= 0) return;
      chargeMasterId = freezeRequestDetails.frz_req_charge_master_id;
      reasonId = freezeRequestDetails.frz_req_reason_id;
    } else {
      // In non-approve mode: use the selected freeze reason's charge_master_id
      const reason = this.FreezeReasonControl?.value;
      if (!reason?.charge_master_id || reason.charge_master_id <= 0){
        this.clearFreezeCharges();
        return;
      };
      chargeMasterId = reason.charge_master_id;
      reasonId = reason.reason_id;
    }

    const dates = this.freezeDateService.prepareDatesForAPI({
      isRecurring: this.IsRecurringSubscription,
      selectedAttempts: this.SelectedFreezeAttempt?.value,
      startDateValue: this.StartDateControl?.value,
      endDateValue: this.EndDateControl?.value,
    });

    if (!dates.length) return;

    const freezeRequestId = this.freezeRequestId();
    const payload: FreezeChargeBreakupRequest = {
      client_id: this.client().clt_id,
      issue_date: this.timeService.ConvertLocalDateTimeToUTC(new Date()),
      is_coupon_code_applied: false,
      request_id: freezeRequestId ?? 0,
      reason_id: reasonId,
      coupon_code: '',
      products: [],
      charges: [
        {
          charge_id: chargeMasterId,
          is_plan_charge: false,
          charge_amount_in_minor: 0,
        },
      ],
    };

    this.freezeChargeLoading$.next(true);

    this.freezeReasonService
      .getFreezeChargeBreakup(
        this.tenantId(),
        this.subscription().id,
        payload
      )
      .subscribe({
        next: (response: PlanBreakUpCalculationResponse) => {
          this.freezeChargeBreakup$.next(response);
          this.processChargeBreakupResponse(response);
          this.freezeChargeLoading$.next(false);
          this.cd.detectChanges();
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.clearFreezeCharges();
          this.freezeChargeLoading$.next(false);
          this.cd.detectChanges();
        },
      });
  }

  private processChargeBreakupResponse(
    response: PlanBreakUpCalculationResponse
  ): void {
    const processed = this.freezeChargeService.processChargeBreakup(
      response,
      this.signupCharges
    );

    if (processed.length > 0) {
      this.signupCharges = processed;
      this.freezeChargeService.setupChargeAmountFormControls(
        this.signupCharges,
        this.ChargeAmountsFormArray,
        this.ChargeDiscountsFormArray
      );
      this.totalChargeAmount =
        this.freezeChargeService.calculateTotalChargeAmount(this.signupCharges);
    } else {
      this.signupCharges = [];
      this.totalChargeAmount = 0;
      this.ChargeAmountsFormArray.clear();
    }
  }

  clearFreezeCharges(): void {
    this.freezeChargeBreakup$.next(null);
    this.signupCharges = [];
    this.totalChargeAmount = 0;
    this.ChargeAmountsFormArray.clear();
    this.ChargeDiscountsFormArray.clear();
    this.discountApplied = false;
    this.inValidDiscount = false;
  }

  hasCharges(): boolean {
    return this.signupCharges.length > 0 && this.totalChargeAmount > 0;
  }

  // ─── Discount Handlers (delegated from child component events) ───────────────
  onChargeAmountBlur(chargeIndex: number): void {
    const result = this.freezeChargeService.applyChargeAmountChange(
      chargeIndex,
      this.signupCharges,
      this.ChargeAmountsFormArray,
      this.ChargeDiscountsFormArray,
      this.single_staff_discount
    );
    this.signupCharges = result.charges;
    this.inValidDiscount = !result.valid;
    if (result.valid) {
      this.totalChargeAmount =
        this.freezeChargeService.calculateTotalChargeAmount(this.signupCharges);
    }
    this.cd.detectChanges();
  }

  onChargeDiscountBlur(chargeIndex: number): void {
    const result = this.freezeChargeService.applyChargeDiscountChange(
      chargeIndex,
      this.signupCharges,
      this.ChargeAmountsFormArray,
      this.ChargeDiscountsFormArray,
      this.single_staff_discount
    );
    this.signupCharges = result.charges;
    this.inValidDiscount = !result.valid;
    if (result.valid) {
      this.totalChargeAmount =
        this.freezeChargeService.calculateTotalChargeAmount(this.signupCharges);
    }
    this.cd.detectChanges();
  }

  onApplyDiscountToggle(): void {
    if (this.applyDiscount) {
      this.ApplyFreezeDiscount();
    } else {
      this.applyDiscount = true;
    }
  }

  ApplyFreezeDiscount(): void {
    if (!this.hasCharges()) return;
    this.applyDiscount = false;
    this.discountApplied = true;
    this.callStaffDiscountCalculation();
  }

  CancelFreezeDiscount(): void {
    this.inValidDiscount = false;
    this.applyDiscount = false;
    this.signupCharges = this.freezeChargeService.resetChargesToOriginal(
      this.signupCharges,
      this.ChargeAmountsFormArray,
      this.ChargeDiscountsFormArray
    );
    this.totalChargeAmount =
      this.freezeChargeService.calculateTotalChargeAmount(this.signupCharges);
    this.cd.detectChanges();
  }

  RemoveFreezeDiscount(): void {
    this.discountApplied = false;
    this.signupCharges = this.freezeChargeService.resetChargesToOriginal(
      this.signupCharges,
      this.ChargeAmountsFormArray,
      this.ChargeDiscountsFormArray
    );
    this.calculateFreezeCharges();
  }

  private callStaffDiscountCalculation(): void {
    const reason = this.FreezeReasonControl?.value;
    if (!reason?.charge_master_id || reason.charge_master_id <= 0) return;

    const request = this.freezeChargeService.buildStaffDiscountRequest(
      this.signupCharges,
      this.client().clt_id,
      this.subscription(),
      this.timeService
    );

    this.freezeChargeLoading$.next(true);

    this.plansubscriptionService
      .getStaffDiscountChargeCalculation(this.tenantId(), request)
      .subscribe({
        next: (response: PlanBreakUpCalculationResponse) => {
          this.freezeChargeBreakup$.next(response);
          this.processChargeBreakupResponse(response);
          this.freezeChargeLoading$.next(false);
          this.discountApplied = true;
          this.cd.detectChanges();
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.freezeChargeLoading$.next(false);
          this.discountApplied = false;
          this.cd.detectChanges();
        },
      });
  }

  // ─── Cycle Adjustment ────────────────────────────────────────────────────────
  calculateFreezeCycleAdjustment(
    selectedCycles?: InvoiceOccurance[]
  ): void {

    const endDateForAdjustment = this.isStopMode && this.StopDateControl?.value
      ? this.StopDateControl.value
      : this.EndDateControl?.value;

    const dates = this.freezeDateService.prepareCycleAdjustmentDates({
      selectedCycles,
      startDateValue: this.StartDateControl?.value,
      endDateValue: endDateForAdjustment,
    });

    if (!dates) return;

    let adjustmentType: RecurringTemplateAdjustmentType;
    let freezeId = 0;

    if (this.isRescheduleMode) {
      adjustmentType = RecurringTemplateAdjustmentType.FreezeReschedule;
      freezeId = this.freezeToReschedule()?.freeze_id ?? 0;
    } else if (this.isStopMode) {
      adjustmentType = RecurringTemplateAdjustmentType.FreezeStop;
      freezeId = this.freezeToReschedule()?.freeze_id ?? 0;
    } else if (this.isCancelMode) {
      adjustmentType = RecurringTemplateAdjustmentType.FreezeCancel;
      freezeId = this.freezeToReschedule()?.freeze_id ?? 0;
    } else {
      adjustmentType = RecurringTemplateAdjustmentType.FullFreeze;
    }

    const payload: CalculateFreezeAdjustmentRequest = {
      requested_start_date: dates.requestedStartDate,
      requested_end_date: dates.requestedEndDate,
      adjustment_type: adjustmentType,
      freeze_id: freezeId,
      is_approved: this.mode() === 'approve' ? true : false,
    };

    const tenantId = this.getTenantId();
    const clientId = this.getClientId();
    const subscriptionId = this.subscription()?.id;

    if (!tenantId || !clientId || !subscriptionId) {
      this.freezeCycleAdjustmentLoading$.next(false);
      return;
    }

    this.freezeCycleAdjustmentLoading$.next(true);

    this.freezeReasonService
      .calculateFreezeAdjustment(tenantId, clientId, subscriptionId, payload)
      .subscribe({
        next: (response: FreezeCycleAdjustmentResponse) => {
          this.freezeCycleAdjustment$.next(response);
          this.freezeCycleAdjustmentLoading$.next(false);
          this.cd.detectChanges();
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.freezeCycleAdjustment$.next(null);
          this.freezeCycleAdjustmentLoading$.next(false);
          this.cd.detectChanges();
        },
      });
  }

  // ─── File Upload ─────────────────────────────────────────────────────────────
  handleFileSelect(files: NgxFileDropEntry[]): void {
    if (!files?.length) return;

    const file = files[0];
    if (file.fileEntry.isFile) {
      const fileEntry = file.fileEntry as FileSystemFileEntry;
      fileEntry.file((uploadedFile: File) => {
        this.processFile(uploadedFile);
      });
    }
  }

  private processFile(file: File): void {
    const purposeCode =
      this.FreezeReasonControl?.value?.reason_code || 'FREEZE_DOCUMENT';

    this.isUploadingFile$.next(true);

    this.freezeFileService
      .processFile(file, purposeCode)
      .then((result) => {
        this.uploadedFiles.push(result);
        this.isUploadingFile$.next(false);
        this.cd.detectChanges();
        this.translate
          .get('FREEZE.FREEZE_SIDEBAR.FILE_UPLOAD_SUCCESS')
          .subscribe((message) => {
            this.toastMessageService.showSuccess(message);
          });
      })
      .catch((errorKey: string) => {
        this.isUploadingFile$.next(false);
        this.translate.get(errorKey, { size: '5MB' }).subscribe((message) => {
          this.toastMessageService.showErrorMessage(message);
        });
        this.cd.detectChanges();
      });
  }

  removeFile(index: number): void {
    if (index >= 0 && index < this.uploadedFiles.length) {
      this.uploadedFiles.splice(index, 1);
      this.cd.detectChanges();
      this.translate
        .get('FREEZE.FREEZE_SIDEBAR.FILE_REMOVED')
        .subscribe((message) => {
          this.toastMessageService.showSuccess(message);
        });
    }
  }

  // ─── Permission Checks ───────────────────────────────────────────────────────
  hasApprovalRights(): boolean {
    return this.freezePermissionService.hasApprovalRights(
      this.currentUserDetails
    );
  }

  isAdmin(): boolean {
    return this.freezePermissionService.isAdmin(this.currentUserDetails);
  }

  isStaff(): boolean {
    return this.freezePermissionService.isStaff(this.currentUserDetails);
  }

  isPaymentRequired(): boolean {
    return this.hasCharges() && this.totalChargeAmount > 0;
  }

  canShowPaymentButtons(): boolean {
    return this.freezePermissionService.canShowPaymentButtons({
      mode: this.mode(),
      isPaymentRequired: this.isPaymentRequired(),
      userDetails: this.currentUserDetails,
    });
  }

  canShowSubmitButton(): boolean {
    return this.freezePermissionService.canShowSubmitButton({
      mode: this.mode(),
      isPaymentRequired: this.isPaymentRequired(),
      userDetails: this.currentUserDetails,
    });
  }

  // ─── Submission ──────────────────────────────────────────────────────────────
  getHeaderText(): string {
    switch (this.mode()) {
      case 'reschedule':
        return 'FREEZE.FREEZE_SIDEBAR.HEADER_RESCHEDULE';
      case 'stop':
        return 'FREEZE.FREEZE_SIDEBAR.HEADER_STOP';
      case 'cancel':
        return 'FREEZE.FREEZE_SIDEBAR.HEADER_CANCEL';
      case 'approve':
        return 'FREEZE.FREEZE_SIDEBAR.HEADER_APPROVE';
      case 'request':
        return 'FREEZE.FREEZE_SIDEBAR.HEADER_REQUEST';
      case 'freeze':
      default:
        return this.hasApprovalRights()
          ? 'FREEZE.FREEZE_SIDEBAR.HEADER_FREEZE'
          : 'FREEZE.FREEZE_SIDEBAR.HEADER_REQUEST';
    }
  }

  getSubmitButtonLabel(): string {
    const mode = this.mode();
    if (mode === 'request') {
      return this.translate.instant('FREEZE.FREEZE_SIDEBAR.BUTTON_REQUEST');
    }
    if (mode === 'freeze') {
      return this.isPaymentRequired()
        ? this.translate.instant('FREEZE.FREEZE_SIDEBAR.BUTTON_CONTINUE_PAYMENT')
        : this.translate.instant('FREEZE.FREEZE_SIDEBAR.BUTTON_APPLY');
    }
    if (mode === 'approve') {
      return this.isPaymentRequired()
        ? this.translate.instant('FREEZE.FREEZE_SIDEBAR.BUTTON_CONTINUE_PAYMENT')
        : this.translate.instant('FREEZE.FREEZE_SIDEBAR.BUTTON_APPROVE');
    }
    return this.translate.instant('FREEZE.FREEZE_SIDEBAR.BUTTON_APPLY');
  }

  getSelectedFreezeReason(): FreezeReason | null {
    const reason = this.FreezeReasonControl?.value;
    if (!reason) return null;
    return (
      this.freezeReasons$.value.find(
        (r) => r.reason_id === reason.reason_id
      ) || null
    );
  }

  private assertFreezeAttemptsAvailable(): boolean {
    const mode = this.mode();
    if (
      mode === 'reschedule' ||
      mode === 'stop' ||
      mode === 'cancel' ||
      mode === 'approve'
    ) {
      return true;
    }

    const subscription = this.subscription();
    const allowed = subscription.freeze_allowed_number_of_attempts_in_year;
    if (!allowed || allowed <= 0) {
      return true;
    }

    const yearWise = getYearWiseFreezeRemaining({
      subscription,
      billingOccurrences: this.freezeTimeline$.value,
      freezes: this.freeze$.value,
    });

    const targetYears = getFreezeTargetYears({
      isRecurring: this.IsRecurringSubscription,
      freezeCanOnlyBillingCycle: subscription.freeze_can_only_billing_cycle,
      selectedAttempts: this.SelectedFreezeAttempt?.value,
      startDate: this.StartDateControl?.value ?? null,
    });

    if (!targetYears.length) {
      return true;
    }

    const validation = validateFreezeYearsHaveRemainingAttempts(
      yearWise,
      targetYears
    );
    if (!validation.valid) {
      this.toastMessageService.showErrorMessage(
        this.translate.instant('FREEZE.NO_FREEZE_ATTEMPTS_REMAINING', {
          year: validation.exhaustedYear ?? new Date().getFullYear(),
        })
      );
      return false;
    }

    return true;
  }

  createFreeze(): void {
    this.fg.markAllAsTouched();
    if (!this.fg.valid) return;
    if (!this.assertFreezeAttemptsAvailable()) return;

    switch (this.mode()) {
      case 'approve':
        this.handleApproveMode();
        break;
      case 'freeze':
        this.handleFreezeMode();
        break;
      case 'reschedule':
        this.handleRescheduleMode();
        break;
      case 'stop':
        this.handleStopMode();
        break;
      case 'cancel':
        this.handleCancelMode();
        break;
      case 'request':
      default:
        this.handleRequestMode();
        break;
    }
  }

  handleApproveMode(): void {
    if (!this.freezeRequestId()) {
      this.toastMessageService.showErrorMessage(
        'Freeze request ID is required for approve mode'
      );
      return;
    }

    const freezeRequest = this.freezeRequestDetails$.value;
    if (!freezeRequest) {
      this.toastMessageService.showErrorMessage(
        'Freeze request details not loaded'
      );
      return;
    }

    const paymentRequired =
      this.isPaymentRequired() ||
      (freezeRequest.frz_req_charge_amount_in_minor > 0);

    if (paymentRequired) return;

    this.isSubmitting$.next(true);
    this.approveFreezeRequest();
  }

  handleFreezeMode(): void {
    if (this.isPaymentRequired()) return;

    this.isSubmitting$.next(true);
    this.submitFreezeRequest();
  }

  handleRequestMode(): void {
    this.isSubmitting$.next(true);
    this.submitFreezeRequest();
  }

  handleRescheduleMode(): void {
    if (!this.freezeToReschedule()) {
      this.toastMessageService.showErrorMessage(
        'Freeze data is required for reschedule mode'
      );
      return;
    }



    const cycleInfo = this.freezeCycleAdjustment$.value;
    if (!cycleInfo) {
      this.toastMessageService.showErrorMessage(
        'Please wait for cycle adjustment calculation to complete'
      );
      return;
    }

    const startDate = this.StartDateControl?.value;
    const endDate = this.EndDateControl?.value;

    if (!startDate || !endDate) {
      this.toastMessageService.showErrorMessage(
        'Start date and end date are required'
      );
      return;
    }

    this.isSubmitting$.next(true);
    this.btnLoading.next(true);

    const endDateCopy = new Date(endDate);
    endDateCopy.setHours(23, 59, 59);

    const payload: RescheduleFreezeRequest = {
      start_date: this.timeService.ConvertLocalDateTimeToUTC(startDate),
      end_date: this.timeService.ConvertLocalDateTimeToUTC(endDateCopy),
      comment: this.fg.get('comment')?.value || '',
      calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle:
        cycleInfo,
    };

    const freeze = this.freezeToReschedule()!;

    this.freezeReasonService
      .rescheduleFreeze(
        this.getTenantId(),
        this.getClientId(),
        this.subscription().id,
        freeze.freeze_id,
        payload
      )
      .subscribe({
        next: () => {
          this.toastMessageService.showSuccess('Freeze rescheduled successfully');
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
          this.isUpdated$.next(true);
          this.HideSidebar();
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
        },
      });
  }

  handleStopMode(): void {
    if (!this.freezeToReschedule()) {
      this.toastMessageService.showErrorMessage(
        'Freeze data is required for stop mode'
      );
      return;
    }

    const freeze = this.freezeToReschedule()!;
    this.isSubmitting$.next(true);
    this.btnLoading.next(true);

    const stopDateValue = this.StopDateControl?.value;
    const stopDate = stopDateValue ? new Date(stopDateValue) : new Date();
    stopDate.setHours(23, 59, 59, 0);

    const payload: StopClientSubscriptionFreezeRequest = {
      freeze_stop_date: this.timeService.ConvertLocalDateTimeToUTC(stopDate),
      stop_reason: this.fg.get('comment')?.value || '',
      calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle:
        this.freezeCycleAdjustment$.value || null,
    };

    this.freezeService
      .PostStopFreeze(
        environment.BASE_URL,
        this.getTenantId(),
        this.getClientId(),
        this.subscription()?.id || 0,
        freeze.freeze_id,
        payload
      )
      .subscribe({
        next: () => {
          this.toastMessageService.showSuccess('Freeze stopped successfully');
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
          this.isUpdated$.next(true);
          setTimeout(() => this.HideSidebar(), 1000);
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
        },
      });
  }

  handleCancelMode(): void {
    if (!this.freezeToReschedule()) {
      this.toastMessageService.showErrorMessage(
        'Freeze data is required for cancel mode'
      );
      return;
    }

    const freeze = this.freezeToReschedule()!;
    this.isSubmitting$.next(true);
    this.btnLoading.next(true);

    const payload: CancelClientSubscriptionFreezeRequest = {
      cancelled_reason: this.fg.get('comment')?.value || '',
      calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle:
        this.freezeCycleAdjustment$.value || null,
    };

    this.freezeService
      .PostCancelFreeze(
        environment.BASE_URL,
        this.getTenantId(),
        this.getClientId(),
        this.subscription()?.id || 0,
        freeze.freeze_id,
        payload
      )
      .subscribe({
        next: () => {
          this.toastMessageService.showSuccess('Freeze cancelled successfully');
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
          this.isUpdated$.next(true);
          setTimeout(() => this.HideSidebar(), 3000);
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
        },
      });
  }

  // ─── Payment ─────────────────────────────────────────────────────────────────
  selectPaymentMode(mode: 'online' | 'offline'): void {
    this.selectedPaymentMode = mode;
    if (mode === 'online') {
      this.handleOnlinePayment();
    } else {
      this.handleOfflinePayment();
    }
  }

  handleOnlinePayment(): void {
    this.selectedPaymentMode = 'online';
    this.posDeviceSale = undefined;
    this.selectedCardOnFileTokenId = null;
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }
    this.isSubmitting$.next(true);
    if (this.mode() === 'approve') {
      this.approveFreezeRequest();
    } else {
      this.submitFreezeWithPayment();
    }
  }

  handleOfflinePayment(): void {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }
    this.selectedPaymentMode = 'offline';
    this.posDeviceSale = undefined;
    this.selectedCardOnFileTokenId = null;
    this.paymentAmount = this.freezeChargeService.getTotalOneTimeCost(this.signupCharges);
    this.creditBalance = 0;
    this.showOtherPaymentsModel$.next(true);
  }

  PosDeviceFreeze(): void {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    if (!this.isAdjustmentCalculated()) {
      return;
    }

    this.selectedCardOnFileTokenId = null;
    this.posGatewaysLoaded = false;
    this.showDeviceList = true;
    this.pendingPosPaymentMode = this.mode() === 'approve' ? 'approve' : 'freeze';
    this.cd.markForCheck();

    this.loadPaymentGatewaysForPos().subscribe({
      next: () => {
        this.posGatewaysLoaded = true;
        this.cd.markForCheck();
      },
      error: (error) => {
        this.toastMessageService.showError(error);
        this.showDeviceList = false;
        this.posGatewaysLoaded = false;
        this.cd.markForCheck();
      },
    });
  }

  OpenSavedPaymentMethods(): void {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    if (!this.isAdjustmentCalculated()) {
      return;
    }

    this.showSavedPaymentMethodsPopup.next(true);
    this.cd.markForCheck();
  }

  onSavedPaymentMethodSelected(tokenId: number): void {
    this.selectedCardOnFileTokenId = tokenId;
    this.pendingPaymentConfirmation = true;
    this.showSavedPaymentMethodsPopup.next(false);
    this.posDeviceSale = undefined;
    this.selectedPaymentMode = null;
    this.transactionInProcess = true;
    this.isSubmitting$.next(true);
    this.btnLoading.next(true);

    if (this.mode() === 'approve') {
      this.approveFreezeRequest();
    } else {
      this.submitFreezeWithPayment();
    }
  }

  onSavedPaymentMethodsPopupClosed(): void {
    this.showSavedPaymentMethodsPopup.next(false);
    if (
      !this.transactionInProcess &&
      !this.paymentConfirmationPending &&
      !this.pendingPaymentConfirmation
    ) {
      this.selectedCardOnFileTokenId = null;
    }
    this.cd.markForCheck();
  }

  private loadPaymentGatewaysForPos() {
    const tenantId = this.getTenantId();

    if (this.stateService.getPaymentGatewayList().length > 0) {
      this.paymentGatewayList.next(this.stateService.getPaymentGatewayList());
      return of(this.stateService.getPaymentGatewayList());
    }

    return this.paymentGatewayMasterService.getPaymentGatewayMasters(tenantId).pipe(
      map((res) => {
        const items = res.items ?? [];
        this.paymentGatewayList.next(items);
        this.stateService.setPaymentGatewayList(items);
        return items;
      })
    );
  }

  private resolvePaymentGatewayForTerminal(
    terminal: PosTerminal
  ): PaymentGatewatMaster | undefined {
    return this.paymentGatewayList.value.find(
      (gateway) => gateway.id === terminal.pgwconfmst_id
    );
  }

  private proceedWithPosTerminal(terminal: PosTerminal): void {
    const gateway = this.resolvePaymentGatewayForTerminal(terminal);
    if (!gateway) {
      this.toastMessageService.showErrorMessage(
        'Required payment gateway is not configured'
      );
      this.isSubmitting$.next(false);
      this.cd.markForCheck();
      return;
    }

    this.paymentGateway = gateway;
    const device: PosDeviceSale = {} as PosDeviceSale;
    device.merchant_id = gateway.merchant_id;
    device.payment_gateway_supported = gateway.payment_gateway;
    device.sale_device_id = terminal.terminal_id;
    this.posDeviceSale = device;
    this.pendingPaymentConfirmation = true;
    this.selectedPaymentMode = null;
    this.transactionInProcess = true;
    this.isSubmitting$.next(true);

    if (this.pendingPosPaymentMode === 'approve') {
      this.approveFreezeRequest();
    } else {
      this.submitFreezeWithPayment();
    }
  }

  closeDevicesPopup(event: PosDeviceListCloseEvent): void {
    this.showDeviceList = false;
    this.posGatewaysLoaded = false;
    this.pendingPosPaymentMode = null;

    if (event.selectedDevice) {
      this.proceedWithPosTerminal(event.selectedDevice);
    } else if (event.emptyList) {
      this.toastMessageService.showErrorMessage('No device configured');
      this.isSubmitting$.next(false);
    } else {
      this.isSubmitting$.next(false);
    }

    this.cd.markForCheck();
  }

  closePaymentFailed(): void {
    this.paymentFailed = false;
    this.paymentFailedMessages = [];
    this.posDeviceSale = undefined;
    this.selectedCardOnFileTokenId = null;
    this.pendingPaymentConfirmation = false;
    this.cd.markForCheck();
  }

  RetryPayment(): void {
    this.paymentFailed = false;
    this.paymentFailedMessages = [];
    this.transactionInProcess = true;
    this.isSubmitting$.next(true);
    this.btnLoading.next(true);

    if (this.selectedCardOnFileTokenId) {
      if (this.mode() === 'approve') {
        this.approveFreezeRequest();
      } else {
        this.submitFreezeWithPayment();
      }
      return;
    }

    if (this.mode() === 'approve') {
      this.approveFreezeRequest();
    } else {
      this.submitFreezeWithPayment();
    }
  }

  HideOtherPaymentDialog(evt: any): void {
    if (evt?.paymentSignUp) {
      if (this.mode() === 'approve') {
        this.approveFreezeRequest(evt.paymentSignUp);
      } else {
        this.submitFreezeWithPayment(evt.paymentSignUp);
      }
    }
    this.showOtherPaymentsModel$.next(false);
  }

  closeOtherPaymentDialog(_event: any): void {
    this.showOtherPaymentsModel$.next(false);
  }

  // ─── Freeze Creation ─────────────────────────────────────────────────────────
  private submitFreezeRequest(): void {
    this.btnLoading.next(true);

    const dates = this.freezeDateService.prepareRequestDates({
      isRecurring: this.IsRecurringSubscription,
      freezeCanOnlyBillingCycle:
        this.subscription().freeze_can_only_billing_cycle,
      selectedAttempts: this.SelectedFreezeAttempt?.value,
      startDateValue: this.StartDateControl?.value,
      endDateValue: this.EndDateControl?.value
        ? (() => {
            const d = new Date(this.EndDateControl!.value);
            d.setHours(23, 59, 59);
            return d;
          })()
        : null,
    });

    const cancelBookingType = this.cancelBookingType();
    const freezeRequest: CreateFreezeRequest = {
      cancel_appt_class_booking: this.getCanceBookingOptionValue(cancelBookingType),
      request_dates: dates,
      comment: this.freezeV2Flow ? '' : this.CommentControl?.value || '',
      frz_req_stf_comment_clt_visible:
        this.ClientVisibleCommentsControl?.value || '',
      frz_req_internal_stf_comment:
        this.InternalCommentsControl?.value || '',
      reason_code: this.FreezeReasonControl?.value?.reason_code || 'Other',
      files: this.freezeFileService.prepareFilesForAPI(this.uploadedFiles),
      sales_rep: this.currentUserDetails.Id,
      issue_date: new Date().toISOString(),
      billing_name: this.client().clt_full_name,
      calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle: this.freezeCycleAdjustment$.value ?? undefined,
    };

    this.freezeReasonService
      .createFreeze(
        this.tenantId(),
        this.client().clt_id,
        this.subscription().id,
        freezeRequest
      )
      .subscribe({
        next: () => this.handleSuccess(),
        error: (error: AppErrors) => this.handleError(error),
      });
  }

  private submitFreezeWithPayment(paymentObj?: PaymentSignUp): void {
    this.btnLoading.next(true);

    const dates = this.freezeDateService.preparePaymentDates({
      isRecurring: this.IsRecurringSubscription,
      freezeCanOnlyBillingCycle:
        this.subscription().freeze_can_only_billing_cycle,
      selectedAttempts: this.SelectedFreezeAttempt?.value,
      startDateValue: this.StartDateControl?.value,
      endDateValue: this.EndDateControl?.value
        ? (() => {
            const d = new Date(this.EndDateControl!.value);
            d.setHours(23, 59, 59);
            return d;
          })()
        : null,
    });

    const cancelBookingType = this.cancelBookingType();
    const isCardOnFile = this.isCardOnFilePayment();
    const freezeRequest: CreateFreezeRequest = {
      cancel_appt_class_booking: this.getCanceBookingOptionValue(cancelBookingType),
      request_dates: dates,
      comment: this.freezeV2Flow ? '' : this.CommentControl?.value || '',
      frz_req_stf_comment_clt_visible:
        this.ClientVisibleCommentsControl?.value || '',
      frz_req_internal_stf_comment:
        this.InternalCommentsControl?.value || '',
      reason_code: this.FreezeReasonControl?.value?.reason_code || 'Other',
      files: this.freezeFileService.prepareFilesForAPI(this.uploadedFiles),
      issue_date: new Date().toISOString(),
      billing_name: this.client().clt_full_name,
      collected_at: paymentObj ? this.paymentAmount : 0,
      is_offline_payment: this.posDeviceSale
        ? false
        : isCardOnFile
          ? false
          : this.selectedPaymentMode === 'offline',
      offline_payment: this.posDeviceSale || isCardOnFile
        ? undefined
        : paymentObj
          ? this.convertPaymentSignUpToOfflinePayment(paymentObj)
          : undefined,
      is_coupon_code_applied: false,
      sigup_coupon_code: '',
      sales_rep: this.currentUserDetails.Id,
      charging_today:
        this.freezeChargeBreakup$.value?.plan_breakup_calculation
          ?.charging_today,
      charging_recurring: null,
      pos_device_sale: this.posDeviceSale,
      card_on_file_token_id: isCardOnFile
        ? this.selectedCardOnFileTokenId ?? undefined
        : undefined,
      calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle:
        this.freezeCycleAdjustment$.value ?? undefined,
    };

    this.freezeReasonService
      .createFreeze(
        this.tenantId(),
        this.client().clt_id,
        this.subscription().id,
        freezeRequest
      )
      .subscribe({
        next: (response: any) => {
          this.handleFreezePaymentApiSuccess(response);
        },
        error: (error: AppErrors) => this.handleError(error),
      });
  }

  approveFreezeRequest(paymentObj?: PaymentSignUp): void {
    const freezeRequestId = this.freezeRequestId();
    if (!freezeRequestId) {
      this.toastMessageService.showErrorMessage(
        'Freeze request ID is required'
      );
      return;
    }

    const freezeRequest = this.freezeRequestDetails$.value;
    if (!freezeRequest) {
      this.toastMessageService.showErrorMessage(
        'Freeze request details not loaded'
      );
      return;
    }

    const { approved_dates, request_dates } =
      this.freezeDateService.prepareApproveDates({
        isRecurring: this.IsRecurringSubscription,
        freezeCanOnlyBillingCycle:
          this.subscription().freeze_can_only_billing_cycle,
        selectedAttempts: this.SelectedFreezeAttempt?.value,
        startDateValue: this.StartDateControl?.value,
        endDateValue: this.EndDateControl?.value
          ? (() => {
              const d = new Date(this.EndDateControl!.value);
              d.setHours(23, 59, 59);
              return d;
            })()
          : null,
        fallbackStartDate: freezeRequest.requested_start_date,
        fallbackEndDate: freezeRequest.requested_end_date,
      });

    const cancelBookingType = this.cancelBookingType();
    const isCardOnFile = this.isCardOnFilePayment();
    const payload: ApproveFreezeRequest = {
      cancel_appt_class_booking: this.getCanceBookingOptionValue(cancelBookingType),
      approved_dates,
      request_dates,
      comment: this.freezeV2Flow ? '' : this.CommentControl?.value || '',
      frz_req_stf_comment_clt_visible:
        this.ClientVisibleCommentsControl?.value || '',
      frz_req_internal_stf_comment:
        this.InternalCommentsControl?.value || '',
      reason_code: this.FreezeReasonControl?.value?.reason_code || 'Other',
      files: this.freezeFileService.prepareFilesForAPI(this.uploadedFiles),
      issue_date: new Date().toISOString(),
      billing_name: this.client().clt_full_name || '',
      collected_at: this.tenantId(),
      is_offline_payment: this.posDeviceSale
        ? false
        : isCardOnFile
          ? false
          : !!paymentObj,
      offline_payment: this.posDeviceSale || isCardOnFile
        ? null
        : paymentObj ?? null,
      is_coupon_code_applied: false,
      sigup_coupon_code: '',
      sales_rep: this.currentUserDetails?.Id || 0,
      is_approved: true,
      charging_today:
        this.freezeChargeBreakup$.value?.plan_breakup_calculation
          ?.charging_today || null,
      charging_recurring: null,
      pos_device_sale: this.posDeviceSale,
      card_on_file_token_id: isCardOnFile
        ? this.selectedCardOnFileTokenId ?? undefined
        : undefined,
      calculated_freeze_cycle_adjustment_info_and_adjusted_future_cycle: this.freezeCycleAdjustment$.value ?? undefined,
    };

    this.freezeReasonService
      .approveFreezeRequest(this.tenantId(), freezeRequestId, payload)
      .subscribe({
        next: (response) => {
          this.handleFreezePaymentApiSuccess(response);
        },
        error: (error) => {
          this.handleError(error);
          this.btnLoading.next(false);
          this.isSubmitting$.next(false);
        },
      });
  }

  private handleFreezePaymentApiSuccess(response: {
    freeze_link_uuid?: string;
  }): void {
    if (response?.freeze_link_uuid) {
      const uuid = this.extractUuidFromFreezeLink(response.freeze_link_uuid);
      if (uuid) {
        this.isSubmitting$.next(false);
        this.btnLoading.next(false);

        if (this.shouldConfirmPaymentAfterApi()) {
          this.handlePaymentConfirmation(uuid, {
            paymentTimeoutMs: this.posDeviceSale
              ? this.POS_PAYMENT_TIMEOUT_MS
              : undefined,
            requireOnlinePaymentStatus: this.isCardOnFilePaymentConfirmation(),
          });
          return;
        }

        this.transactionInProcess = false;
        this.posDeviceSale = undefined;
        this.selectedCardOnFileTokenId = null;
        this.hideSideBar.emit(true);
        this.openPaymentTimeline.emit({ uuid });
        return;
      }
    }

    this.transactionInProcess = false;
    this.posDeviceSale = undefined;
    this.selectedCardOnFileTokenId = null;
    this.handleSuccess();
  }

  private isCardOnFilePayment(): boolean {
    return this.selectedCardOnFileTokenId !== null;
  }

  private isCardOnFilePaymentConfirmation(): boolean {
    return (
      this.selectedCardOnFileTokenId !== null ||
      (this.pendingPaymentConfirmation && !this.posDeviceSale)
    );
  }

  // ─── Button State Helpers ────────────────────────────────────────────────────
  isOnlineBtnDisabled(): boolean {
    return (
      !this.fg.valid ||
      this.isSubmitting$.value ||
      !this.isAdjustmentCalculated()
    );
  }

  isOfflineBtnDisabled(): boolean {
    return (
      !this.fg.valid ||
      this.isSubmitting$.value ||
      !this.isAdjustmentCalculated()
    );
  }

  isAdjustmentCalculated(): boolean {
    if (this.freezeCycleAdjustmentLoading$.value) return false;

    const hasDateSelection =
      this.StartDateControl?.value && this.EndDateControl?.value;
    const hasCycleSelection =
      this.SelectedFreezeAttempt?.value?.length > 0;

    if (!hasDateSelection && !hasCycleSelection) return true;

    return this.freezeCycleAdjustment$.value !== null;
  }

  // ─── Sidebar Lifecycle ───────────────────────────────────────────────────────
  checkInitialLoadingComplete(): void {
    const subscriptionLoaded = !this.subscriptionLoading$.value;
    const freezeReasonsLoaded = !this.freezeReasonsLoading$.value;
    const freezeRequestLoaded =
      this.mode() === 'approve' ? !this.freezeRequestLoading$.value : true;

    if (subscriptionLoaded && freezeReasonsLoaded && freezeRequestLoaded) {
      this.isInitialLoading$.next(false);
    }
  }

  HideSidebar(): void {
    if (this.isPaymentFlowBlockingUi) {
      return;
    }

    this.hideSideBar.emit(false);
    this.hideSideBar.emit(this.isUpdated$.value);
  }

  get isPaymentFlowBlockingUi(): boolean {
    return this.transactionInProcess || this.paymentConfirmationPending;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeyDuringPayment(event: Event): void {
    if (this.paymentConfirmationTimedOut) {
      event.preventDefault();
      this.closePaymentConfirmationTimeout();
      return;
    }

    if (this.isPaymentFlowBlockingUi) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private handlePaymentConfirmation(
    orderUuid: string,
    options?: { paymentTimeoutMs?: number; requireOnlinePaymentStatus?: boolean }
  ): void {
    combineLatest([
      this.store.select(COMPANY_INFO),
      this.store.select(TENANTS_INFO),
    ])
      .pipe(
        take(1),
        filter(
          ([companyInfo, tenantInfo]) =>
            !!companyInfo?.compDetails?.uuid &&
            !!tenantInfo?.selected_tenant?.tenant_uuid
        )
      )
      .subscribe(([companyInfo, tenantInfo]) => {
        const requireOnlinePaymentStatus =
          options?.requireOnlinePaymentStatus ?? false;

        this.transactionInProcess = false;
        this.paymentConfirmationPending = true;
        this.paymentConfirmationTimedOut = false;
        this.paymentConfirmationOrderUuid = orderUuid;
        this.paymentConfirmationPosFlow = !!options?.paymentTimeoutMs;
        this.pendingPaymentConfirmation = true;
        this.paymentConfirmationShowTimeLimit = !!options?.paymentTimeoutMs;
        if (options?.paymentTimeoutMs) {
          this.startPaymentConfirmationCountdown(options.paymentTimeoutMs);
        } else {
          this.paymentConfirmationSecondsRemaining = 0;
        }
        this.cd.markForCheck();

        this.orderSubscriptionStatusPollingService.pollSignupPaymentUntilTerminal({
          companyUuid: companyInfo.compDetails.uuid,
          tenantUuid: tenantInfo.selected_tenant.tenant_uuid,
          uuid: orderUuid,
          deadlineMs: options?.paymentTimeoutMs,
          requireOnlinePaymentStatus,
          onOutcome: (outcome, response) =>
            this.handlePaymentPollOutcome(outcome, orderUuid, response),
        });
      });
  }

  private handlePaymentPollOutcome(
    outcome: SignupPaymentPollTerminalOutcome,
    orderUuid: string,
    response?: GetOrderStatusOfQuotation
  ): void {
    this.clearPaymentConfirmationCountdown();
    this.paymentConfirmationShowTimeLimit = false;
    this.paymentConfirmationSecondsRemaining = 0;

    if (outcome === 'success') {
      this.paymentConfirmationPending = false;
      this.paymentConfirmationTimedOut = false;
      this.posDeviceSale = undefined;
      this.selectedCardOnFileTokenId = null;
      this.pendingPaymentConfirmation = false;
      this.openPaymentTimelineAfterConfirmation(orderUuid, {
        skipPaymentPolling: true,
      });
    } else if (outcome === 'timeout') {
      this.paymentConfirmationOrderUuid = orderUuid;
      this.paymentConfirmationTimedOut = true;
      this.posDeviceSale = undefined;
      this.pendingPaymentConfirmation = false;
    } else {
      this.paymentConfirmationPending = false;
      this.paymentConfirmationTimedOut = false;
      const failureReason = response
        ? getOnlinePaymentFailureReason(response)
        : undefined;

      this.paymentFailedMessages = [
        failureReason || this.translate.instant('POS_DEVICE.FAILED_REASON'),
      ];
      this.pendingPaymentConfirmation = false;
      this.paymentFailed = true;
    }

    this.cd.markForCheck();
  }

  closePaymentConfirmationTimeout(): void {
    const orderUuid = this.paymentConfirmationOrderUuid;
    this.paymentConfirmationPending = false;
    this.paymentConfirmationTimedOut = false;
    this.paymentConfirmationOrderUuid = '';
    this.paymentConfirmationPosFlow = false;
    this.pendingPaymentConfirmation = false;
    this.posDeviceSale = undefined;

    if (orderUuid) {
      this.openPaymentTimelineAfterConfirmation(orderUuid);
    }

    this.cd.markForCheck();
  }

  private openPaymentTimelineAfterConfirmation(
    uuid: string,
    options?: { skipPaymentPolling?: boolean }
  ): void {
    if (this.isPaymentFlowBlockingUi) {
      return;
    }

    this.hideSideBar.emit(true);
    this.openPaymentTimeline.emit({
      uuid,
      skipPaymentPolling: options?.skipPaymentPolling ?? false,
    });
  }

  get paymentConfirmationTimeoutMessage(): string {
    return this.translate.instant(
      this.paymentConfirmationPosFlow
        ? 'POS_DEVICE.PAYMENT_CONFIRMATION_TIMEOUT_POS'
        : 'POS_DEVICE.PAYMENT_CONFIRMATION_TIMEOUT'
    );
  }

  private startPaymentConfirmationCountdown(durationMs: number): void {
    this.clearPaymentConfirmationCountdown();
    const deadline = Date.now() + durationMs;
    this.paymentConfirmationSecondsRemaining = Math.ceil(durationMs / 1000);

    this.paymentConfirmationCountdownSub = interval(1000).subscribe(() => {
      this.paymentConfirmationSecondsRemaining = Math.max(
        0,
        Math.ceil((deadline - Date.now()) / 1000)
      );
      this.cd.markForCheck();
    });
  }

  private clearPaymentConfirmationCountdown(): void {
    if (this.paymentConfirmationCountdownSub) {
      this.paymentConfirmationCountdownSub.unsubscribe();
      this.paymentConfirmationCountdownSub = undefined;
    }
  }

  get paymentConfirmationCountdownDisplay(): string {
    const totalSeconds = this.paymentConfirmationSecondsRemaining;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private shouldConfirmPaymentAfterApi(): boolean {
    return (
      !!this.posDeviceSale ||
      this.selectedCardOnFileTokenId !== null ||
      this.pendingPaymentConfirmation
    );
  }

  IsUpdated(isUpdated: boolean): void {
    if (isUpdated) {
      this.GetFreezeTimeline(this.client().clt_id, this.subscription().id);
      this.isUpdated$.next(isUpdated);
    }
  }

  HideErrorPopup(_event: any): void {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
  }

  // ─── Tenant/Client Context ───────────────────────────────────────────────────
  getTenantId(): number {
    if (
      (this.isRescheduleMode || this.isStopMode || this.isCancelMode) &&
      this.freezeToReschedule()
    ) {
      return this.freezeToReschedule()!.tenant_id;
    }
    return this.tenantId();
  }

  getClientId(): number {
    if (
      (this.isRescheduleMode || this.isStopMode || this.isCancelMode) &&
      this.freezeToReschedule()
    ) {
      return this.freezeToReschedule()?.client_id || 0;
    }
    return this.client()?.clt_id || 0;
  }

  // ─── Utility Methods ─────────────────────────────────────────────────────────
  getLatestComment(comments: any[]): string {
    if (!comments?.length) return '';
    return (
      [...comments].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )[0].comment || ''
    );
  }

  toDate(d: Date | string): string {
    const dateStr = new Date(d).toDateString();
    const dateStrArr = dateStr.split(' ');
    return `<span>${this.ordinalNumber(
      dateStrArr[2] as unknown as number
    )}</span><span> of </span><span>${dateStrArr[1]}</span><span>${dateStrArr[3]}</span>`;
  }

  ordinalNumber(n: number | null | undefined): string {
    if (!n) return '';
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return (
      n.toString() +
      '<sup>' +
      (s[(v - 20) % 10] || s[v] || s[0]).toString() +
      '</sup> '
    );
  }

  client2LetterName(name: string): string {
    if (!name) return '';
    const parts = name.split(' ').filter(Boolean);
    return parts.slice(0, 2).join(' ');
  }

  clientStatus(client: Client | null): string {
    if (client && (client.clt_status || (client as any)?.client_status)) {
      return this.clientService.getClientStatusHtml(
        (client.clt_status || '').toString()
      );
    }
    return '';
  }

  ishavefreezeToRescheduleObject(): boolean {
    return this.freezeToReschedule() !== null && this.freezeToReschedule() !== undefined && Object.keys(this.freezeToReschedule() ?? {}).length > 0;
  }

  private handleSuccess(shouldRedirect = true): void {
    this.transactionInProcess = false;
    this.posDeviceSale = undefined;
    this.isUpdated$.next(true);
    this.translate
      .get('FREEZE.FREEZE_SIDEBAR.REQUEST_SUCCESS')
      .subscribe((translation) => {
        this.toastMessageService.showSuccess(translation);
      });
    this.hideSideBar.emit(false);
    this.btnLoading.next(false);
    this.isSubmitting$.next(false);
    this.hideSideBar.emit(this.isUpdated$.value);
    if (shouldRedirect) {
      this.router.navigate(['/client-management/freeze']);
    }
  }

  private handleError(error: AppErrors): void {
    const messages = extractApiErrorMessages(error);

    if (this.transactionInProcess) {
      this.transactionInProcess = false;
      this.paymentFailedMessages = messages;
      this.paymentFailed = true;
      this.btnLoading.next(false);
      this.isSubmitting$.next(false);
      this.cd.markForCheck();
      return;
    }

    if (messages.length > 0) {
      this.error.next(error);
      this.errorMessages = messages;
      this.showErrorPopup.next(true);
    } else {
      this.toastMessageService.showError(error);
    }

    this.btnLoading.next(false);
    this.isSubmitting$.next(false);
    this.cd.markForCheck();
  }

  private extractUuidFromFreezeLink(freezeLinkUrl: string): string | null {
    if (!freezeLinkUrl) return null;
    const paylinkIndex = freezeLinkUrl.indexOf('/paylink/');
    if (paylinkIndex === -1) return null;
    const uuid = freezeLinkUrl.substring(paylinkIndex + '/paylink/'.length);
    return uuid.split('?')[0].split('#')[0] || null;
  }

  private convertPaymentSignUpToOfflinePayment(paymentObj: PaymentSignUp): any {
    return {
      vpa_id: paymentObj.vpa_id || '',
      upi_provider: paymentObj.upi_provider || '',
      network_type: paymentObj.network_type || '',
      card_number: paymentObj.card_number || '',
      bank_name: paymentObj.bank_name || '',
      auth_code: paymentObj.auth_code || '',
      cheque_date: paymentObj.cheque_date || new Date().toISOString(),
      exp_date: paymentObj.exp_date || new Date().toISOString(),
      is_credit_balance_used: paymentObj.is_credit_balance_used || false,
      credit_balance_amount_in_minor_currency:
        paymentObj.credit_balance_amount_in_minor_currency || 0,
      amount_in_minor_currency:
        paymentObj.amount_in_minor_currency || this.paymentAmount,
      rounded_amount_in_minor_currency:
        paymentObj.rounded_amount_in_minor_currency || this.paymentAmount,
      paymode_level1: paymentObj.paymode_level1 || 1,
      paymode_level2_id: paymentObj.paymode_level2_id || 1,
    };
  }

  getConvertedDate(date: any): string {
    const localDate = this.timeService.ConvertUTCToLocalDateTime(date);
    return this.timeService.ConvertLocalDateTimeToUTC(localDate);
  }

  getCanceBookingOptionValue(cancelBookingType: string): CancelAppointmentClassBooking {
    if (cancelBookingType === 'CancelAll') {
      return CancelAppointmentClassBooking.CancelAll;
    }
    if (cancelBookingType === 'CancelSubscriptionApptClassBooking') {
      return CancelAppointmentClassBooking.CancelSubscriptionApptClassBooking;
    }
    return CancelAppointmentClassBooking.None;
  }
}
